import React from 'react';
import { X, Save, RotateCcw, Database, FileText, BarChart3, Tag, User, Globe, Calendar, ExternalLink } from 'lucide-react';
import { formatRelatedLinksForDisplay } from '@shared/related-links';

interface SavePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  originalData: any;
  editedData: any;
  tempImageData?: any;
}

const SavePreviewModal: React.FC<SavePreviewModalProps> = ({
  isOpen,
  onClose,
  onSave,
  originalData,
  editedData,
  tempImageData
}) => {
  if (!isOpen) return null;

  const getFieldChanges = () => {
    const changes: { field: string; original: any; edited: any; type: string }[] = [];
    
    // Basic fields (excluding imageId for now, we'll handle it separately)
    const basicFields = ['title', 'lead', 'description', 'location', 'language', 'author', 'studyFlow', 'highlights', 'conclusion', 'radar', 'metaTitle', 'metaDescription', 'ogImage', 'canonicalUrl'];
    basicFields.forEach(field => {
      if (originalData[field] !== editedData[field]) {
        changes.push({
          field,
          original: originalData[field],
          edited: editedData[field],
          type: 'basic'
        });
      }
    });

    // Handle image changes separately
    const hasImageChange = tempImageData || (originalData.imageId !== editedData.imageId);
    if (hasImageChange) {
      changes.push({
        field: 'imageId',
        original: originalData.imageId ? 'Imagen existente' : 'Sin imagen',
        edited: tempImageData ? 'Nueva imagen temporal' : (editedData.imageId ? 'Imagen existente' : 'Sin imagen'),
        type: 'basic'
      });
    }

    // Tags
    if (JSON.stringify(originalData.tags) !== JSON.stringify(editedData.tags)) {
      changes.push({
        field: 'tags',
        original: originalData.tags?.join(', ') || 'Ninguno',
        edited: editedData.tags?.join(', ') || 'Ninguno',
        type: 'tags'
      });
    }

    // Keywords
    if (JSON.stringify(originalData.keywords) !== JSON.stringify(editedData.keywords)) {
      changes.push({
        field: 'keywords',
        original: originalData.keywords?.join(', ') || 'Ningunas',
        edited: editedData.keywords?.join(', ') || 'Ningunas',
        type: 'tags'
      });
    }

    // Highlights
    if (JSON.stringify(originalData.highlights || []) !== JSON.stringify(editedData.highlights || [])) {
      changes.push({
        field: 'highlights',
        original: originalData.highlights?.length ? `${originalData.highlights.length} puntos destacados` : 'Ninguno',
        edited: editedData.highlights?.length ? `${editedData.highlights.length} puntos destacados` : 'Ninguno',
        type: 'highlights'
      });
    }

    // Conclusion
    if (originalData.conclusion !== editedData.conclusion) {
      changes.push({
        field: 'conclusion',
        original: originalData.conclusion ? 'Conclusión definida' : 'Sin conclusión',
        edited: editedData.conclusion ? 'Conclusión definida' : 'Sin conclusión',
        type: 'conclusion'
      });
    }

    // Related Links
    if (JSON.stringify(originalData.relatedLinks || []) !== JSON.stringify(editedData.relatedLinks || [])) {
      changes.push({
        field: 'relatedLinks',
        original: originalData.relatedLinks?.length ? `${originalData.relatedLinks.length} enlaces relacionados` : 'Ninguno',
        edited: editedData.relatedLinks?.length ? `${editedData.relatedLinks.length} enlaces relacionados` : 'Ninguno',
        type: 'relatedLinks'
      });
    }

    // Blocks
    if (JSON.stringify(originalData.data.blocks) !== JSON.stringify(editedData.data.blocks)) {
      changes.push({
        field: 'blocks',
        original: `${originalData.data.blocks.length} bloques`,
        edited: `${editedData.data.blocks.length} bloques`,
        type: 'blocks'
      });
    }

    // Segments
            if (JSON.stringify(originalData.data?.segments || []) !== JSON.stringify(editedData.data?.segments || [])) {
      changes.push({
        field: 'segments',
                    original: `${originalData.data?.segments?.length || 0} segmentaciones`,
            edited: `${editedData.data?.segments?.length || 0} segmentaciones`,
        type: 'segments'
      });
    }

    // Charts
    if (JSON.stringify(originalData.data?.charts || []) !== JSON.stringify(editedData.data?.charts || [])) {
      changes.push({
        field: 'charts',
        original: `${originalData.data?.charts?.length || 0} gráficos`,
        edited: `${editedData.data?.charts?.length || 0} gráficos`,
        type: 'charts'
      });
    }

    return changes;
  };

  const changes = getFieldChanges();

  const formatChangeValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return 'Vacío';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return formatRelatedLinksForDisplay(value);
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const renderFieldChange = (change: any) => {
    return (
      <div key={change.field} className="border border-surface-300 rounded-lg p-4 mb-3">
        <div className="flex items-center gap-2 mb-2">
          {change.type === 'basic' && <FileText size={16} className="text-primary" />}
          {change.type === 'tags' && <Tag size={16} className="text-primary" />}
          {change.type === 'highlights' && <Tag size={16} className="text-primary" />}
          {change.type === 'conclusion' && <FileText size={16} className="text-primary" />}
          {change.type === 'relatedLinks' && <ExternalLink size={16} className="text-primary" />}
          {change.type === 'blocks' && <BarChart3 size={16} className="text-primary" />}
          {change.type === 'segments' && <BarChart3 size={16} className="text-primary" />}
          {change.type === 'charts' && <BarChart3 size={16} className="text-primary" />}
          <span className="font-medium capitalize">{change.field.replace(/([A-Z])/g, ' $1').trim()}</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-muted-foreground mb-1">Valor Original:</div>
            <div className="text-sm bg-surface-100 p-2 rounded border">
              {change.type === 'relatedLinks'
                ? formatRelatedLinksForDisplay(change.original)
                : formatChangeValue(change.original)}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground mb-1">Nuevo Valor:</div>
            <div className="text-sm bg-green-50 p-2 rounded border border-green-200">
              {change.type === 'relatedLinks'
                ? formatRelatedLinksForDisplay(change.edited)
                : formatChangeValue(change.edited)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDatabasePreview = () => {
    // Campos que van a la tabla principal
    const tableFields = {
      id: editedData.id,
      location: editedData.location,
      language: editedData.language,
      slug: editedData.slug,
      title: editedData.title,
      lead: editedData.lead,
      description: editedData.description,
      studyFlow: editedData.studyFlow,
      highlights: editedData.highlights,
      conclusion: editedData.conclusion,
      relatedLinks: editedData.relatedLinks,
      tags: editedData.tags,
      imageUrl: editedData.imageUrl,
      author: editedData.author,
      status: editedData.status,
      // SEO Fields
      metaTitle: editedData.metaTitle,
      metaDescription: editedData.metaDescription,
      keywords: editedData.keywords,
      ogImage: editedData.ogImage,
      canonicalUrl: editedData.canonicalUrl,
      createdAt: editedData.createdAt,
      updatedAt: new Date().toISOString()
    };

    // Campos que van al JSONB data
    const jsonbData = editedData.data;

    return (
      <div className="space-y-4">
        {/* Tabla Principal */}
        <div className="border border-surface-300 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Database size={16} className="text-primary" />
            <span className="font-medium">Campos de la Tabla Principal</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground mb-1">ID:</div>
              <div className="bg-surface-100 p-2 rounded border font-mono text-xs">
                {tableFields.id}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Slug:</div>
              <div className="bg-surface-100 p-2 rounded border font-mono text-xs">
                {tableFields.slug}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Título:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.title}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Autor:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.author}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Ubicación:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.location}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Idioma:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.language}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Estado:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.status}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Tags:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.tags?.join(', ') || 'Ninguno'}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-muted-foreground mb-1">Descripción:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.description}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-muted-foreground mb-1">Metodología:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.studyFlow}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-muted-foreground mb-1">Puntos Destacados:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.highlights?.length ? `${tableFields.highlights.length} puntos destacados` : 'Ninguno'}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-muted-foreground mb-1">Conclusión:</div>
              <div className="bg-surface-100 p-2 rounded border">
                {tableFields.conclusion ? 'Conclusión definida' : 'Sin conclusión'}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-muted-foreground mb-1">URL de Imagen:</div>
              <div className="bg-surface-100 p-2 rounded border font-mono text-xs">
                {tableFields.imageUrl || 'No especificada'}
              </div>
            </div>
            
            {/* SEO Fields */}
            <div className="md:col-span-2 border-t border-surface-300 pt-4 mt-4">
              <h4 className="font-medium text-foreground mb-3">Campos SEO</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-muted-foreground mb-1">Meta Title:</div>
                  <div className="bg-surface-100 p-2 rounded border text-xs">
                    {tableFields.metaTitle || 'No especificado'}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-1">Meta Description:</div>
                  <div className="bg-surface-100 p-2 rounded border text-xs">
                    {tableFields.metaDescription || 'No especificada'}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-1">Keywords:</div>
                  <div className="bg-surface-100 p-2 rounded border text-xs">
                    {tableFields.keywords?.join(', ') || 'No especificadas'}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-1">OG Image:</div>
                  <div className="bg-surface-100 p-2 rounded border font-mono text-xs">
                    {tableFields.ogImage || 'No especificada'}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-muted-foreground mb-1">Canonical URL:</div>
                  <div className="bg-surface-100 p-2 rounded border font-mono text-xs">
                    {tableFields.canonicalUrl || 'No especificada'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* JSONB Data */}
        <div className="border border-surface-300 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Database size={16} className="text-primary" />
            <span className="font-medium">JSONB Data (blocks, segments, evolution)</span>
          </div>
          <pre className="text-xs bg-surface-100 p-3 rounded border overflow-x-auto max-h-64 overflow-y-auto">
            {JSON.stringify(jsonbData, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <Database size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Vista Previa - Guardar Cambios</h2>
              <p className="text-sm text-muted-foreground">
                Revisa todos los cambios antes de guardar en la base de datos
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {changes.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-muted-foreground mb-4">
                No se detectaron cambios para guardar
              </div>
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                Cerrar
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 size={16} className="text-blue-600" />
                  <span className="font-medium text-blue-800">Resumen de Cambios</span>
                </div>
                <p className="text-sm text-blue-700">
                  Se han detectado <strong>{changes.length}</strong> campos modificados que se guardarán en la base de datos.
                </p>
              </div>

              {/* Changes List */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Campos Modificados</h3>
                {changes.map(renderFieldChange)}
              </div>

              {/* Database Preview */}
              {renderDatabasePreview()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-surface-50 flex-shrink-0">
          <div className="text-sm text-muted-foreground">
            {changes.length} cambios pendientes
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Cancelar
            </button>
            <button
              onClick={onSave}
              className="btn-primary flex items-center gap-2"
              disabled={changes.length === 0}
            >
              <Save size={16} />
              Guardar en Base de Datos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavePreviewModal; 